<template>
    <modal :name="name"
           height="95%"
           width="95%"
           scrollable
           :clickToClose="false"
           @before-open="beforeOpen"
           @before-close="beforeClose">

        <div class="h-100 overflow-auto">

            <div v-if="isLoading" class="row mx-0 h-100">
                <div class="col align-self-center text-center">
                    <h5 class="text-muted">
                        Loading...
                    </h5>
                </div>
            </div>

            <template v-else>
                <div class="row m-0 text-left border-bottom rounded-top bg-white p-3">
                    <div class="col-8 align-self-center pl-0">
                        <h5 class="m-0">
                            Form templates
                        </h5>
                    </div>
                    <div class="col-4 align-self-center text-right">
                        <button @click="onSelectTemplate"
                                :disabled="!selectedTemplate"
                                class="btn btn-sm btn-outline-primary"
                                style="width: 120px">
                            Select
                        </button>
                        <button @click="cancelSelect"
                                class="btn btn-sm btn-outline-info ml-3"
                                style="width: 80px">
                            Cancel
                        </button>
                    </div>
                </div>

                <splitpanes class="default-theme w-100" style="height: calc(100% - 64px)">
                    <pane size="40" min-size="20" max-size="75" style="min-width: 240px; overflow-y: auto">
                        <div class="p-3">
                            <div v-for="(template, idx) in templates"
                                 @click="onPreviewTemplate(idx)"
                                 class="template-item"
                                 :class="{ 'selected': selectedTemplateIdx === idx }">
                                {{ template.display }}
                                <div v-if="template.isDefault" class="float-right text-muted font-12">
                                    Default
                                </div>
                                <div v-else-if="template.global" class="float-right text-muted font-12">
                                    Global
                                </div>
                            </div>
                        </div>
                    </pane>
                    <pane size="60" min-size="40" max-size="75" class="border-left pt-2">
                        <div v-if="!selectedTemplate" class="text-muted px-4 py-3">
                            Select a template
                        </div>
                        <DocumentViewerForm v-else :settings="selectedTemplate.settings"/>
                    </pane>
                </splitpanes>
            </template>
        </div>
    </modal>
</template>

<script>
import axios from 'axios'
import {ERROR} from '../../../../../store/modules/ui/UI'
import {purchaseFormSettingsTemplate} from './models/purchase'
import DocumentViewerForm from '../../../single/view/DocumentViewerForm'
import {Pane, Splitpanes} from 'splitpanes'
import {withConfirmation} from '../../../../../decorators/withConfirmation'
import {statementFormSettingsTemplate} from './models/statement'
import {batchDocsFormSettingsTemplate} from './models/batches'
import {mapMutations} from 'vuex'
import {DOC_SETTINGS_UPDATE} from '../../../../../store/modules/doc-viewer/settings'

export default {
    name: 'FormTemplates',
    components: {
        DocumentViewerForm,
        Splitpanes,
        Pane
    },
    props: {
        name: {
            type: String,
            required: true
        },
        section: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            selectedTemplateIdx: null,
            templates: [{
                property: 'default-purchase',
                display: 'Purchase',
                settings: purchaseFormSettingsTemplate,
                isDefault: true
            }, {
                property: 'default-statement',
                display: 'Statement',
                settings: statementFormSettingsTemplate,
                isDefault: true
            }, {
                property: 'default-batches',
                display: 'Batches',
                settings: batchDocsFormSettingsTemplate,
                isDefault: true
            }],
            isLoading: false
        }
    },
    computed: {
        selectedTemplate() {
            return this.selectedTemplateIdx !== -1 ? this.templates[this.selectedTemplateIdx] : null
        }
    },
    methods: {
        ...mapMutations('doc_viewer_settings', {
            updateSettings: DOC_SETTINGS_UPDATE
        }),

        beforeClose() {
            Object.assign(this.$data, this.$options.data.apply(this))
        },

        async beforeOpen({params}) {
            this.isLoading = true
            await this.fetchTemplates()
            this.isLoading = false
        },

        fetchTemplates() {
            return axios.get(`documents/types/${this.section}/subtypes`, {params: {isTemplate: true}})
                .then(res => {
                    if (res && res.data && _.isArray(res.data.data)) {
                        this.templates = this.templates.concat(res.data.data)
                    }
                })
                .catch(() => {
                    this.$snackbar.show({type: ERROR})
                })
        },

        onPreviewTemplate(idx) {
            this.selectedTemplateIdx = idx
            const settings = _.cloneDeep(this.selectedTemplate.settings) || {}
            this.updateSettings({prop: 'sections', data: settings.sections})
            this.updateSettings({prop: 'fields', data: settings.fields})
            this.updateSettings({prop: 'extensions', data: settings.extensions})
        },

        onSelectTemplate() {
            withConfirmation({
                onConfirm: this.selectTemplate,
                title: 'Select template',
                description: 'The selected template will replace the existing form</br>Do you want to continue?'
            })
        },

        selectTemplate() {
            this.$emit('select', _.cloneDeep(this.selectedTemplate.settings))
            this.$nextTick(() => {
                this.closeModal()
            })
        },

        cancelSelect() {
            this.$emit('cancel')
            this.closeModal()
        },

        closeModal() {
            this.$modal.hide(this.name)
        }
    }
}
</script>

<style scoped lang="scss">
    button {
        width: 120px;
    }

    .template-item {
        font-weight: 500;
        font-size: 16px;
        border-radius: 4px;
        border: 1px solid $color-grey-light;
        padding: 8px;
        margin-bottom: 4px;

        &:hover {
            cursor: pointer;
        }

        &.selected {
            background: $color-purple-soft;
        }
    }
</style>
